/** @jsxImportSource @compiled/react */
/* eslint-disable react/no-danger -- when updating this component, consider replacing dangerous jsx properties */
import React, { useRef, useLayoutEffect, useCallback } from 'react';

// The Connect-owned sibling selector is not supported by @atlaskit/css's strict API.
import { css } from '@compiled/react';

import { evalLegacyConnectInlineScripts } from '@confluence/connect-utils';
import { expValEquals } from '@confluence/feature-experiments';
import { useExtensionDimensions } from '@confluence/browser-metrics/entry-points/useExtensionDimensions.ts';
import { usePageState } from '@confluence/page-context';
import { ATL_GENERAL } from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';

type WebPanelProps = {
	html: string;
	/**
	 * Unique identifier for this web panel instance
	 */
	panelId?: string;
	panelLocation?: string;
};

const backgroundPanelStyles = css({
	// Connect owns this injected DOM. Keep background apps running without loading chrome
	// taking up space. Timeout/error contents remain visible, without the loading padding.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Connect owns these injected elements, so styles cannot be passed as React props.
	'& iframe.ap-iframe[width="0"][height="0"] ~ .ap-status-indicator, & iframe.ap-iframe[width="0"][height="0px"] ~ .ap-status-indicator, & iframe.ap-iframe[width="0px"][height="0"] ~ .ap-status-indicator, & iframe.ap-iframe[width="0px"][height="0px"] ~ .ap-status-indicator':
		{
			paddingTop: 0,
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Connect owns these injected elements, so styles cannot be passed as React props.
	'& iframe.ap-iframe[width="0"][height="0"] ~ .ap-status-indicator > .ap-loading, & iframe.ap-iframe[width="0"][height="0px"] ~ .ap-status-indicator > .ap-loading, & iframe.ap-iframe[width="0px"][height="0"] ~ .ap-status-indicator > .ap-loading, & iframe.ap-iframe[width="0px"][height="0px"] ~ .ap-status-indicator > .ap-loading':
		{
			display: 'none',
		},
});

export function WebPanel({ html, panelId, panelLocation }: WebPanelProps): React.JSX.Element {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [{ contentId, spaceKey }] = usePageState();
	const dimensionsRef = useExtensionDimensions(
		panelLocation === ATL_GENERAL ? ATL_GENERAL : undefined,
		panelId,
		contentId || spaceKey || '',
	);
	const panelRef = useCallback(
		(element: HTMLDivElement | null) => {
			containerRef.current = element;
			dimensionsRef(element);
		},
		[dimensionsRef],
	);
	useLayoutEffect(() => {
		const container = containerRef.current;

		if (!container) {
			return;
		}

		void evalLegacyConnectInlineScripts(container);
	}, [html, panelId]);

	return (
		<div
			ref={panelRef}
			css={[
				panelLocation === ATL_GENERAL &&
				expValEquals('cc_connect_zero_size_panel_loading', 'isEnabled', true)
					? backgroundPanelStyles
					: undefined,
			]}
			data-vc-ignore-if-no-layout-shift
			dangerouslySetInnerHTML={{ __html: html }}
		/>
	);
}
